import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Drawer,
  Paper,
  Theme,
  Typography,
} from "@material-ui/core";
import { Add as AddIcon } from "@material-ui/icons";
import ExportIcon from "@material-ui/icons/SaveAlt";
import { makeStyles } from "@material-ui/styles";
import clsx from "clsx";
import _ from "lodash";
import * as React from "react";
import { useAlert } from "react-alert";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { useCallback, useMemo, useRef, useState } from "react";

import { downloadEventSchema } from "../components/EventCSVDownloadButton";
import { EventType } from "../eventTypes";
import Navigation from "../Navigation";
import { LOAD_EVENTS } from "../reducers/events";
import { StoreState } from "../store";
import { handleToggleDestinations } from "./api";
import { EventEditorDrawer } from "./components/EditorDrawer/EventEditorDrawer";
import SegmentCompatibleNotificationModal from "./components/SegmentCompatibleNotificationModal";
import TableHeader from "./components/TableHeader";
import { isDestinationToggleable } from "./utils";
import useArrayOfDestinations from "../hooks/useArrayOfDestinations";
import { useAllSaasDestinations } from "../hooks/useAllSaasDestinations";
import { warehouseDestinations } from "../destinations/destinations";
import { filterEvents, FilterSettings } from "./filtering";
import { LoadedEventDefinition } from "../types/global";
import { LibraryTable } from "./components/LibraryTable";

const EVENTS_LIBRARY_BASE = "/events/library";

const TAG_FILTER_KEY = "tagFilter";
const TYPE_FILTER_KEY = "typeFilter";
const DESTINATIONS_FILTER_KEY = "destinationsFilter";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    // 64px for the height of the navbar and
    // 24px for the padding of the navigation content
    height: "calc(100vh - 64px - 24px)",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    paddingBottom: theme.spacing(2),
    position: "relative",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  breadcrumbs: {
    "& a, & p": {
      textTransform: "uppercase",
      fontWeight: 700,
    },
    marginLeft: theme.spacing(-3),
  },
  headerButtonsContainer: {
    display: "grid",
    marginLeft: theme.spacing(1),
  },
  createEventButton: {
    marginBottom: theme.spacing(2),
  },
  accentButton: {
    backgroundColor: "#3F51B5",
    color: "white",
    "&:hover": {
      backgroundColor: "#2A3678",
    },
    "& path": {
      fill: "white",
    },
  },
  icon: {
    width: 16,
    marginRight: theme.spacing(1.5),
  },
  navigation: {
    paddingTop: theme.spacing(3),
  },
  listContainer: {
    backgroundColor: "white",
    overflow: "auto",
    position: "relative",
    maxHeight: "calc(100vh - 64px - 24px)",
  },
  tableHeader: {
    color: "#444444",
    fontSize: "0.875rem",
    fontWeight: 700,
  },
  logoContainer: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "white",
    border: "1px solid rgba(63, 81, 181, 0.1)",
  },
  headerSpacer: {
    margin: `0 ${theme.spacing(2)}px`,
    height: 40,
    width: 1,
    backgroundColor: "#D8D8D8",
  },
  loadingWrapper: {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
  },
  showHidden: {
    height: 80,
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    fontSize: "1rem",
    color: "#444444",
    position: "sticky",
    top: 0,
    zIndex: 20,
    backgroundColor: "white",
  },
}));

const ProgressBar = () => {
  return (
    <Box
      height={120}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <CircularProgress />
    </Box>
  );
};

function LibraryPage() {
  // React Hooks
  const containerRef = useRef<HTMLDivElement>(null);
  const classes = useStyles();
  const alert = useAlert();
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const urlParams = useParams<{ eventDefID: string }>();

  // URL parsing
  const searchParams = useMemo(() => new URLSearchParams(location.search), [
    location,
  ]);

  // State
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showHidden, setShowHidden] = useState(false);
  const [nonToggleableEventIds, setNonToggleableEventIds] = useState<
    string[] | null
  >(null);

  const eventDetailsId = urlParams.eventDefID || null;
  const [showEventEditor, setShowEventEditor] = useState(!!eventDetailsId);

  // Additional hooks/state
  const allSaasDestinations = useAllSaasDestinations(); // loads function destinations too
  let destinations = useArrayOfDestinations();
  let { eventDefs, loading: eventDefsLoading } = useSelector(
    (state: StoreState) => state.eventDefs
  );

  /////////////////////////////////////////////////////
  // Pre-process data
  /////////////////////////////////////////////////////
  const configurableDestinations = useMemo(() => {
    return { ...warehouseDestinations, ...allSaasDestinations };
  }, [allSaasDestinations]);

  const allTags = useMemo(() => {
    const result: Record<string, true> = {};

    for (let eventDef of eventDefs) {
      for (let tag in eventDef.tags) {
        result[tag] = true;
      }
    }

    return result;
  }, [eventDefs]);

  /////////////////////////////////////////////////////
  // Parse filter parameters from URL
  /////////////////////////////////////////////////////
  // The destinationFilter is a list of destination IDs for a given set of types
  // The structure of DESTINATIONS_FILTER_KEY is `type1,type2,type3`
  const destinationFilter = useMemo(() => {
    return (
      searchParams
        .get(DESTINATIONS_FILTER_KEY)
        ?.split(",")
        .filter((a) => a.length > 0) || []
    );
  }, [searchParams]);

  const typeFilter = useMemo(
    () => searchParams.get(TYPE_FILTER_KEY)?.split(",") || [],
    [searchParams]
  );

  const tagFilter = useMemo(
    () => searchParams.get(TAG_FILTER_KEY)?.split(",") || [],
    [searchParams]
  );

  /////////////////////////////////////////////////////
  // Select subset of events using filters
  /////////////////////////////////////////////////////
  const events = useMemo(() => {
    // TODO: Below, we do a typecast in Typescript to change typeFilter to
    // Array<EventType>, but we have no runtime validation.
    const filterSettings: FilterSettings = {
      searchQuery: searchQuery,
      destinationSettings: destinationFilter,
      typeSettings: typeFilter as Array<EventType>,
      tagSettings: tagFilter,
      showHidden: showHidden,
    };

    return filterEvents(
      eventDefs,
      destinations,
      configurableDestinations,
      filterSettings
    );
  }, [
    eventDefs,
    destinations,
    configurableDestinations,
    searchQuery,
    destinationFilter,
    typeFilter,
    tagFilter,
    showHidden,
  ]);

  const eventsByID = useMemo(() => _.keyBy(events, (e) => e.id), [events]);

  /////////////////////////////////////////////////////
  // Define handlers
  /////////////////////////////////////////////////////
  const InvalidEventForwardingAlert = ({ invalidIds }) => (
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <span>{`The changes could not be applied to ${invalidIds.length} events.`}</span>
      <Button
        variant="text"
        onClick={() => {
          setNonToggleableEventIds(invalidIds);
        }}
        style={{ color: "white" }}
      >
        Why?
      </Button>
    </Box>
  );

  const toggleEventForwarding = useCallback(
    (destinationId?: string, shouldForwardEvents: boolean = true) => {
      const destination = _.values(destinations).find(
        (d) => d.id === destinationId
      );

      if (!destination) {
        console.debug(`Destination not found: ${destinationId}`);
        return;
      }

      // We only want to toggle an event if
      // (1) it's not currently in the correct state
      // (2) it's not already being sent by default

      const isWarehouse = warehouseDestinations[destination.type_slug];
      const eventsToBeToggled = selectedEvents
        .filter(
          (evId) =>
            eventsByID[evId]?.enabled_dests[destination.id] !==
            shouldForwardEvents
        )
        .filter((evId) =>
          isDestinationToggleable(eventsByID[evId], isWarehouse)
        );

      handleToggleDestinations(
        destination.id,
        eventsToBeToggled,
        shouldForwardEvents
      )
        .then(() => {
          dispatch({
            type: LOAD_EVENTS,
            payload: eventDefs.map((e) =>
              selectedEvents.includes(e.id)
                ? {
                    ...e,
                    enabled_dests: shouldForwardEvents
                      ? {
                          ...e.enabled_dests,
                          [destination.id]: shouldForwardEvents,
                        }
                      : _.omit(e.enabled_dests, destination.id),
                  }
                : e
            ),
          });

          shouldForwardEvents
            ? alert.success(
                `Forwarding ${eventsToBeToggled.length} event definitions to ${destination?.name}`
              )
            : alert.info(
                `No longer forwarding ${eventsToBeToggled.length} event definitions to ${destination?.name}`
              );

          const invalidEventIds = selectedEvents.filter(
            (evId) => !isDestinationToggleable(eventsByID[evId], isWarehouse)
          );

          if (invalidEventIds.length > 0) {
            alert.info(
              <InvalidEventForwardingAlert invalidIds={invalidEventIds} />,
              {
                timeout: 0,
              }
            );
          }
        })
        .catch(() => {
          alert.error("Something went wrong");
        });
    },
    [destinations, selectedEvents, eventsByID, dispatch, eventDefs, alert]
  );

  const toggleSelectAllEvents = (e: React.ChangeEvent<HTMLInputElement>) => {
    const allEventIds = events.map((event: LoadedEventDefinition) => event.id);
    setSelectedEvents(e.target.checked ? allEventIds : []);
  };

  const handleCloseModal = useCallback(() => {
    setShowEventEditor(false);
    history.push(EVENTS_LIBRARY_BASE);
  }, [setShowEventEditor, history]);

  ///////////////////////////////////////////
  // Actual component
  ///////////////////////////////////////////
  return (
    <Navigation className={clsx(classes.navigation)}>
      <div ref={containerRef} className={clsx(classes.container)}>
        <Box className={clsx(classes.header)}>
          <Box>
            <Typography
              component="h1"
              variant="h3"
              className={clsx(classes.title)}
            >
              Events Library
            </Typography>
            <Typography component="h6" variant="subtitle1">
              Manage your Freshpaint events in one central location. Select one
              or more events to see bulk editing options.
            </Typography>
          </Box>
          <Box className={clsx(classes.headerButtonsContainer)}>
            <Button
              variant="contained"
              className={clsx(classes.createEventButton, classes.accentButton)}
              onClick={() => setShowEventEditor(true)}
              style={{ marginBottom: 10 }}
            >
              <AddIcon className={classes.icon} />
              Create New Event
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => downloadEventSchema(events, destinations)}
            >
              <ExportIcon className={classes.icon} />
              Export All Events
            </Button>
          </Box>
        </Box>

        <Paper className={clsx(classes.listContainer)}>
          <TableHeader
            typeFilter={typeFilter}
            destinationFilter={destinationFilter}
            configurableDestinations={configurableDestinations}
            tagFilter={tagFilter}
            totalEvents={events.length}
            selectedEvents={selectedEvents}
            allEvents={eventDefs}
            events={events}
            onClearSelection={() => {
              setSelectedEvents([]);
            }}
            toggleEventForwarding={toggleEventForwarding}
            showHidden={showHidden}
            setShowHidden={setShowHidden}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
          />
          <Box height={56 * 11} maxHeight={56 * 11}>
            <LibraryTable
              setSelectedEvents={setSelectedEvents}
              selectedEvents={selectedEvents}
              eventDefs={eventDefs}
              events={events}
              configurableDestinations={configurableDestinations}
              allTags={allTags}
              toggleSelectAllEvents={toggleSelectAllEvents}
              onClickRow={({ rowData }) => {
                history.push(EVENTS_LIBRARY_BASE + "/" + rowData.id);
                setShowEventEditor(true);
              }}
            />
          </Box>
          {eventDefsLoading && <ProgressBar />}
        </Paper>
      </div>

      <Drawer open={showEventEditor} onClose={handleCloseModal} anchor="right">
        <Box width={720}>
          <EventEditorDrawer
            onClose={handleCloseModal}
            event={events.find((ev) => ev.id === eventDetailsId)}
          />
        </Box>
      </Drawer>

      <Dialog
        open={!!nonToggleableEventIds}
        onClose={() => {
          setNonToggleableEventIds(null);
        }}
      >
        <SegmentCompatibleNotificationModal
          eventIds={nonToggleableEventIds}
          onClose={() => setNonToggleableEventIds(null)}
          eventsByID={eventsByID}
        />
      </Dialog>
    </Navigation>
  );
}

export default LibraryPage;
